import { supabase } from '@/lib/supabase'
import { qontoApiCall, getQontoStatus } from './qontoApi'
import type { Contact, ContactFormData, ContactFilters } from '@/types/contact'

/**
 * Build the Qonto client body with correct field names
 * - company: { name, type: 'company' }
 * - individual: { first_name, last_name, type: 'individual' }
 */
/**
 * Build Qonto client payload from a users table row.
 * Column names match the `users` table: phone, address, adresse_facturation.
 */
export function buildQontoClientBody(user: {
  full_name?: string | null; email?: string | null; company?: string | null; type_client?: string | null;
  phone?: string | null; address?: string | null; adresse_facturation?: string | null;
  tva_intracommunautaire?: string | null; siret?: string | null;
}) {
  const isCompany = user.type_client === 'professionnel' && user.company
  const parts = (user.full_name || '').trim().split(/\s+/)
  const firstName = parts[0] || 'Client'
  const lastName = parts.slice(1).join(' ') || '-'

  // Parse address — try adresse_facturation first, fallback to address
  const rawAddress = user.adresse_facturation || user.address || ''
  // Try to extract zip+city from "street, 75009 Paris" or "street 75009 Paris"
  const zipCityMatch = rawAddress.match(/(\d{5})\s+(.+?)$/)
  const streetPart = zipCityMatch ? rawAddress.slice(0, zipCityMatch.index).replace(/,?\s*$/, '') : rawAddress
  const zipCode = zipCityMatch?.[1] || ''
  const city = zipCityMatch?.[2] || ''

  const base: Record<string, unknown> = {
    email: user.email || '',
    first_name: firstName,
    last_name: lastName,
    locale: 'FR',
    currency: 'EUR',
  }

  // Only send billing_address if we have actual data
  if (streetPart || zipCode || city) {
    base.billing_address = {
      street_address: streetPart || '-',
      city: city || '-',
      zip_code: zipCode || '00000',
      country_code: 'FR',
    }
  }

  if (user.phone) {
    const raw = user.phone.replace(/\s+/g, '')
    if (raw.startsWith('+')) {
      const match = raw.match(/^(\+\d{1,3})(.+)$/)
      base.phone = { country_code: match?.[1] || '+33', number: match?.[2] || raw }
    } else {
      base.phone = { country_code: '+33', number: raw.replace(/^0/, '') }
    }
  }

  if (user.tva_intracommunautaire) {
    base.vat_number = user.tva_intracommunautaire
  }
  if (user.siret) {
    base.tax_identification_number = user.siret
  }

  if (isCompany) {
    return { ...base, name: user.company, kind: 'company', type: 'company' }
  }
  return { ...base, kind: 'individual', type: 'individual' }
}

export const contactsService = {
  async getAll(filters?: ContactFilters): Promise<Contact[]> {
    let query = supabase
      .from('users')
      .select('*')
      .eq('role', 'client')
      .order('created_at', { ascending: false })

    if (filters?.search) {
      query = query.or(
        `full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,company.ilike.%${filters.search}%`
      )
    }

    if (filters?.type) {
      query = query.eq('type_client', filters.type)
    }

    if (filters?.source) {
      query = query.eq('source', filters.source)
    }

    if (filters?.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags)
    }

    const { data, error } = await query

    if (error) throw error
    return data as Contact[]
  },

  async getById(id: string): Promise<Contact | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .eq('role', 'client')
      .single()

    if (error) throw error
    return data as Contact
  },

  async create(contact: ContactFormData): Promise<Contact> {
    const { data, error } = await supabase
      .from('users')
      .insert({ ...contact, role: 'client' })
      .select()
      .single()

    if (error) throw error
    const created = data as Contact

    // Auto-sync to Qonto (non-blocking)
    try {
      const status = await getQontoStatus()
      if (status.connected) {
        const qontoClient = buildQontoClientBody(created)
        const result = await qontoApiCall<{ client: { id: string } }>('/clients', {
          method: 'POST',
          body: qontoClient,
        })

        if (result?.client?.id) {
          await supabase
            .from('users')
            .update({ qonto_client_id: result.client.id })
            .eq('id', created.id)
        }
      }
    } catch (err) {
      console.error('[Qonto] Failed to sync new contact:', err)
    }

    return created
  },

  async update(id: string, contact: Partial<ContactFormData>): Promise<Contact> {
    const { data, error } = await supabase
      .from('users')
      .update({ ...contact, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('role', 'client')
      .select()
      .single()

    if (error) throw error
    const updated = data as Contact

    // Auto-sync to Qonto (non-blocking)
    try {
      const status = await getQontoStatus()
      if (status.connected) {
        const qontoBody = buildQontoClientBody(updated)
        if (updated.qonto_client_id) {
          // Update existing Qonto client
          await qontoApiCall(`/clients/${updated.qonto_client_id}`, {
            method: 'PATCH',
            body: qontoBody,
          })
        } else {
          // Create new Qonto client
          const result = await qontoApiCall<{ client: { id: string } }>('/clients', {
            method: 'POST',
            body: qontoBody,
          })
          if (result?.client?.id) {
            await supabase.from('users').update({ qonto_client_id: result.client.id }).eq('id', id)
          }
        }
      }
    } catch (err) {
      console.error('[Qonto] Failed to sync updated contact:', err)
    }

    return updated
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id)
      .eq('role', 'client')

    if (error) throw error
  },

  async syncToQonto(userId: string): Promise<void> {
    const { data: user, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .eq('role', 'client')
      .single()

    if (error) throw error
    if (!user) throw new Error('Contact introuvable')

    const status = await getQontoStatus()
    if (!status.connected) throw new Error('Qonto non connecte')

    const qontoBody = buildQontoClientBody(user)

    if ((user as any).qonto_client_id) {
      // Update existing Qonto client
      await qontoApiCall(`/clients/${(user as any).qonto_client_id}`, {
        method: 'PATCH',
        body: qontoBody,
      })
    } else {
      // Create new Qonto client
      const result = await qontoApiCall<{ client: { id: string } }>('/clients', {
        method: 'POST',
        body: qontoBody,
      })

      if (result?.client?.id) {
        const { error: updateError } = await supabase
          .from('users')
          .update({ qonto_client_id: result.client.id })
          .eq('id', userId)

        if (updateError) throw updateError
      }
    }
  },

  async syncAllToQonto(): Promise<{ synced: number; errors: number }> {
    const status = await getQontoStatus()
    if (!status.connected) throw new Error('Qonto non connecte')

    const { data: users, error } = await supabase
      .from('users')
      .select('*')
      .eq('role', 'client')
      .is('qonto_client_id', null)

    if (error) throw error
    if (!users || users.length === 0) return { synced: 0, errors: 0 }

    let synced = 0
    let errors = 0

    for (const user of users) {
      try {
        const qontoClient = buildQontoClientBody(user)

        const result = await qontoApiCall<{ client: { id: string } }>('/clients', {
          method: 'POST',
          body: qontoClient,
        })

        if (result?.client?.id) {
          await supabase
            .from('users')
            .update({ qonto_client_id: result.client.id })
            .eq('id', user.id)
          synced++
        }
      } catch (err) {
        console.error(`[Qonto] Failed to sync user ${user.id}:`, err)
        errors++
      }
    }

    return { synced, errors }
  },
}
